import { observer } from 'mobx-react-lite'
import { Input, Tabs } from '@gate/gui'
import { useTranslation } from 'next-i18next'
import { HOME_PAGE_URL } from '@/constants/index'
import { useStore } from '@/store/index'
import { eventTracker } from '@/utils/collectEvent'
import usePageRouter from '@/hooks/usePageRouter'
import { StoreProps } from '@/store/baseStore'
import { FC, useMemo, useState } from 'react'
import { PriceTableItemProps } from '..'
import useLangPrefix from '@/hooks/useLangPrefix'
import { stopEvent } from '@/utils/tools'
import { Flex } from '@mantine/core'
import { useAdapter } from '@/components/Adapter'
import { useUpdateEffect } from 'ahooks'
import SearchList from './SearchList'
import FundingFilter from './FundingFilter'
import { TitleTabsPStyled, TitleTabsStyled, TitleTabStyled, FundraisingInputStyled } from './style'

const titleTabsRecordMap = {
  [HOME_PAGE_URL.Market]: 'market',
  [HOME_PAGE_URL.Sectors]: 'sectors',
  [HOME_PAGE_URL.Fundraising]: 'fundraising',
}

const TitleTabs: FC<PriceTableItemProps & { onInputChange?: (value: string) => void }> = ({
  onChangeTab,
  onInputChange,
}) => {
  const { t } = useTranslation()
  const { isH5 } = useAdapter()
  const { langPrefix } = useLangPrefix()
  const { titleTab, setTitleTab, priceTab, setPriceTab } = useStore().baseStore
  const { changePageType } = usePageRouter()
  const [searchValue, setSearchValue] = useState<string>('')

  const onTabChange = async (tab: StoreProps['titleTab']) => {
    if (tab === titleTab) {
      return
    }
    await setTitleTab(tab)

    if (tab === HOME_PAGE_URL.Sectors) {
      onChangeTab(HOME_PAGE_URL.Sectors)
      changePageType(HOME_PAGE_URL.Sectors)
      return
    }

    if (tab === HOME_PAGE_URL.Fundraising) {
      onChangeTab(HOME_PAGE_URL.Fundraising)
      changePageType(HOME_PAGE_URL.Fundraising)
      return
    }

    if (Object.values(HOME_PAGE_URL).includes(priceTab)) {
      if ((priceTab as any) === HOME_PAGE_URL.Sectors || (priceTab as any) === HOME_PAGE_URL.Fundraising) {
        await setPriceTab(HOME_PAGE_URL.Market)
        changePageType(HOME_PAGE_URL.Market)
        onChangeTab(HOME_PAGE_URL.Market)
      } else {
        await setPriceTab(priceTab)
        changePageType(priceTab)
        onChangeTab(priceTab)
      }
    } else {
      await setPriceTab(priceTab)
      changePageType(`/price/view/${priceTab}` as any)
      onChangeTab(HOME_PAGE_URL.Others, priceTab)
    }
  }

  // todo 搜索框切换默认搜索条件清除
  useUpdateEffect(() => {
    setSearchValue('')
  }, [priceTab])

  /**
   * @description: 元素h5 变化配置
   */
  const titleTabsConf = useMemo((): Record<string, any> => {
    if (titleTab === HOME_PAGE_URL.Fundraising && isH5) {
      return {
        align: 'unset;',
      }
    }

    return {
      align: 'center',
    }
  }, [titleTab, isH5])

  return (
    <TitleTabsStyled>
      <Flex w={'100%'} justify="space-between" align={titleTabsConf.align} gap={{ base: 16, sm: 24 }}>
        <Tabs className="price-fundraising-title-tabs" type="primary" value={titleTab} onTabChange={onTabChange}>
          <Tabs.List className="price-fundraising-title-tabs">
            <Tabs.Tab value={HOME_PAGE_URL.Market}>
              <a
                onClick={e => {
                  stopEvent(e)
                  eventTracker('price_sectors_click', {
                    tab_name: titleTabsRecordMap[HOME_PAGE_URL.Market],
                  })
                  onTabChange(HOME_PAGE_URL.Market)
                }}
                href={`${langPrefix}${HOME_PAGE_URL.Market}`}
              >
                <TitleTabStyled>{t('P.Market')}</TitleTabStyled>
              </a>
            </Tabs.Tab>

            <Tabs.Tab value={HOME_PAGE_URL.Sectors}>
              <a
                onClick={e => {
                  stopEvent(e)
                  eventTracker('price_sectors_click', {
                    tab_name: titleTabsRecordMap[HOME_PAGE_URL.Sectors],
                  })
                  onTabChange(HOME_PAGE_URL.Sectors)
                }}
                href={`${langPrefix}${HOME_PAGE_URL.Sectors}`}
              >
                <TitleTabStyled>{t('P.Sectors')}</TitleTabStyled>
              </a>
            </Tabs.Tab>

            <Tabs.Tab value={HOME_PAGE_URL.Fundraising}>
              <a
                onClick={e => {
                  stopEvent(e)
                  eventTracker('price_sectors_click', {
                    tab_name: titleTabsRecordMap[HOME_PAGE_URL.Fundraising],
                  })
                  onTabChange(HOME_PAGE_URL.Fundraising)
                }}
                href={`${langPrefix}${HOME_PAGE_URL.Fundraising}`}
              >
                <TitleTabStyled>{t('p.fundraising', { ns: 'fundraising' })}</TitleTabStyled>
              </a>
            </Tabs.Tab>
          </Tabs.List>
        </Tabs>

        {titleTab === HOME_PAGE_URL.Fundraising ? (
          <>
            {isH5 ? (
              <FundingFilter />
            ) : (
              <FundraisingInputStyled>
                <Input
                  size="md"
                  styles={{ input: { width: isH5 ? '100%' : 240 } }}
                  value={searchValue}
                  icon={
                    <svg className="iconpark-icon size-14">
                      <use href="#v31-16px-line-sousuo"></use>
                    </svg>
                  }
                  placeholder={t('P.Search') + ''}
                  onChange={(e: any) => {
                    const value = e.target.value
                    setSearchValue(value)
                    onInputChange?.(value)
                  }}
                  onClear={() => {
                    setSearchValue('')
                    onInputChange?.('')
                  }}
                />
              </FundraisingInputStyled>
            )}
          </>
        ) : (
          <SearchList />
        )}
      </Flex>

      {titleTab === HOME_PAGE_URL.Fundraising && isH5 ? (
        <FundraisingInputStyled>
          <Input
            size="md"
            styles={{ input: { width: isH5 ? '100%' : 240 } }}
            value={searchValue}
            icon={
              <svg className="iconpark-icon size-14">
                <use href="#v31-16px-line-sousuo"></use>
              </svg>
            }
            placeholder={t('P.Search') + ''}
            onChange={(e: any) => {
              const value = e.target.value
              setSearchValue(value)
              onInputChange?.(value)
            }}
            onClear={() => {
              setSearchValue('')
              onInputChange?.('')
            }}
          />
        </FundraisingInputStyled>
      ) : null}

      {titleTab !== HOME_PAGE_URL.Fundraising && <TitleTabsPStyled>{t('P.24hourTrading')}</TitleTabsPStyled>}
    </TitleTabsStyled>
  )
}

export default observer(TitleTabs)
